import { useEffect } from "react"

import type { IntelligentBaselineResponseDto } from "~/api/services/genability/genability.types"
import type { UseProposal } from "~/containers/Proposal/state/proposal/proposal-context"

import type { MonthData } from "../components/types"

interface EstimateSourceData {
  type: `annual` | `bill` | `default`
  annualUsage?: number
  billAmount?: number
  billStartMonth?: number
  billEndMonth?: number
}

interface AutoGenerateParams {
  accountId: string | undefined
  proposal: ReturnType<UseProposal>
  monthlyData: MonthData[]
  estimateFromAnnual: (
    annualUsage: number,
  ) => Promise<IntelligentBaselineResponseDto>
  estimateFromBill: (
    billAmount: number,
    monthIndex: number,
    billEndMonth?: number,
  ) => Promise<IntelligentBaselineResponseDto>
  setEstimateSourceData: (data: EstimateSourceData) => void
  viewMode?: string
  onError?: (message: string | null) => void
}

export const useAutoGenerateEstimates = ({
  accountId,
  proposal,
  monthlyData,
  estimateFromAnnual,
  estimateFromBill,
  setEstimateSourceData,
  viewMode,
  onError,
}: AutoGenerateParams): void => {
  useEffect(() => {
    if (!accountId) return

    // Don't auto-generate when user is in manual entry mode
    const isInManualEntryFlow =
      viewMode === `selectEntry` ||
      viewMode === `annualForm` ||
      viewMode === `billForm`
    if (isInManualEntryFlow) return

    const hasGridData = monthlyData.some((month) => month.source !== `empty`)
    const hasAnnualData = (proposal.local.annualEnergyUse || 0) > 0
    const hasMonthlyData = proposal.local.monthlyEnergyCost?.some(
      (cost) => cost > 0,
    )

    // Check if we have stored data that should be used for pre-fill instead
    const hasStoredData =
      proposal.local.monthlyEnergyCost?.some((cost) => cost > 0) ||
      proposal.local.monthlyEnergyUse?.some((usage) => usage > 0)

    // Only auto-generate if we have proposal data but no grid data yet AND no stored data
    if (!hasGridData && (hasAnnualData || hasMonthlyData) && !hasStoredData) {
      if (hasAnnualData) {
        onError?.(null)
        // Generate from annual usage
        estimateFromAnnual(proposal.local.annualEnergyUse ?? 0)
          .then(() => {
            // Only show annual usage in estimate source if there's no monthly data
            // (indicating the annual usage was user-provided, not calculated from monthly estimates)
            if (!hasMonthlyData) {
              setEstimateSourceData({
                type: `annual`,
                annualUsage: proposal.local.annualEnergyUse ?? 0,
              })
            } else {
              // If we have both annual and monthly data, the annual is likely calculated
              // from monthly estimates, so show generic "Smart estimates" instead
              setEstimateSourceData({
                type: `default`,
              })
            }
          })
          .catch((error) => {
            console.error(`Failed to auto-generate annual estimates:`, error)
            const errorMessage =
              error instanceof Error && error.message
                ? error.message
                : `Failed to auto-generate annual estimates. Please try again later.`
            onError?.(
              `Unable to auto-generate energy profile from annual usage. ${errorMessage}`,
            )
          })
      } else if (hasMonthlyData) {
        // Find first non-zero monthly cost for bill estimation
        const firstNonZeroIndex = proposal.local.monthlyEnergyCost.findIndex(
          (cost) => cost > 0,
        )

        if (firstNonZeroIndex !== -1) {
          const billAmount = proposal.local.monthlyEnergyCost[firstNonZeroIndex]

          onError?.(null)
          estimateFromBill(billAmount, firstNonZeroIndex, firstNonZeroIndex)
            .then(() => {
              setEstimateSourceData({
                type: `default`,
              })
            })
            .catch((error) => {
              console.error(`Failed to auto-generate monthly estimates:`, error)
              const errorMessage =
                error instanceof Error && error.message
                  ? error.message
                  : `Failed to auto-generate monthly estimates. Please try again later.`
              onError?.(
                `Unable to auto-generate energy profile from monthly data. ${errorMessage}`,
              )
            })
        }
      }
    }
  }, [
    accountId,
    proposal.local.annualEnergyUse,
    proposal.local.monthlyEnergyCost,
    proposal.local.monthlyEnergyUse,
    monthlyData,
    estimateFromAnnual,
    estimateFromBill,
    setEstimateSourceData,
    viewMode,
    onError,
  ])
}
